/**
 * skyAgent (SkyCRM) — session API slice.
 *
 * Scope: the NON-streaming half of Contract B (STATUS-skyagent.md §2). The chat
 * stream itself is deliberately NOT an RTK endpoint: it is SSE-over-POST (an
 * `Authorization` header is required, which `EventSource` cannot send), so it is
 * read with `fetch` + a stream reader inside `useSkyAgentChat`. RTK Query has no
 * incremental-response model, and forcing one here would buffer the whole turn
 * and destroy the streaming UX.
 *
 *   POST /crm/skyagent/sessions      → { sessionId }
 *   GET  /crm/skyagent/sessions      → { sessions: [...] }      (history rail)
 *   GET  /crm/skyagent/sessions/:id  → { messages: [...] }      (one thread)
 *
 * CAPABILITY GATE: every one of these may answer `{ enabled: false }` instead of
 * its payload when the tenant's capability key is off (§2, "zero DB reads and
 * zero model calls"). That is a NORMAL response, not an error, so the response
 * types model it as a union and the UI renders a disabled state rather than an
 * error state. `baseApi` injects `instanceId` + the bearer token.
 */
import { baseApi } from "./baseApi";

/**
 * FROZEN — Contract B (STATUS-skyagent.md §2). This is skySchedule's chunk shape
 * verbatim so the FE port stays near-identical. Do NOT extend this union in
 * Track B; if it is wrong, it goes back to the STATUS doc as a blocker.
 *
 * NOTE `toolCall` is carried on the wire but is NEVER RENDERED: tool activity
 * shows the server-rendered humanLabel in `content` only. A raw tool name or an
 * endpoint path must never reach the screen (§2 Contract A, `humanLabel`).
 */
export type ChatChunk = {
  type: "thinking" | "tool_start" | "tool_progress" | "tool_complete" | "text" | "result" | "error";
  content: string;
  toolCall?: { name?: string };
  /**
   * How the turn ended, structured — on the FINAL chunk only. An OPTIONAL
   * FIELD, not a member of the frozen `type` union: a server that predates it
   * simply never sends it, and the rail renders exactly as before. Mirrors
   * `server/services/crmAgent/turnOutcome.ts`; the two are kept in step by
   * hand because the client does not import server modules.
   */
  outcome?: TurnOutcome;
};

/** Mirrors the runner's `StopReason`. */
export type TurnStopReason =
  | "final"
  | "event_cap"
  | "wall_clock"
  | "budget"
  | "error"
  | "empty"
  | "aborted";

export type TurnNoticeKind =
  | "budget"
  | "event_cap"
  | "wall_clock"
  | "tool_timed_out"
  | "repeat_served"
  | "repeat_refused"
  | "error";

/**
 * One thing the user should know about how the turn ended. `tool` is the raw
 * tool NAME and is NEVER RENDERED — the same rule as `toolCall.name` above;
 * `label` is the server humanLabel and is the only one of the two that may be
 * shown.
 */
export interface TurnNotice {
  kind: TurnNoticeKind;
  tool?: string;
  label?: string;
  /** On a `budget` notice: `daily` when the workspace daily budget ended the
   *  turn, absent when the per-question ceiling did. */
  scope?: "daily";
}

export interface TurnOutcome {
  stopReason: TurnStopReason;
  /** Empty for a turn that finished normally. */
  notices: TurnNotice[];
}

export interface ChatSessionSummary {
  sessionId: string;
  title: string;
  /** Server field name. Was declared `updatedAt`, which the API never sends, so
   *  every rail row rendered a blank date. Hand-written response types cannot be
   *  checked against the server, so this drift is invisible to tsc. */
  lastActivityAt: string | null;
  /** The community selected when this thread was started; null = all communities.
   *  The rail badges a thread whose scope differs from the current selection, so
   *  yesterday's numbers are attributable to the scope that produced them. */
  communityId?: string | null;
  communityName?: string | null;
}

export interface ChatSessionMessage {
  role: "user" | "assistant";
  content: string;
}

/** A capability-gated payload: either the data, or the fail-closed off-switch. */
export type Gated<T> = ({ enabled?: true } & T) | { enabled: false };

export type SessionListResponse = Gated<{
  sessions: ChatSessionSummary[];
  /** Another page exists beyond the rows returned. Page size is 20, matching
   *  skySchedule's `fetchSessions(limit = 20, offset = 0)`. */
  has_more?: boolean;
  /**
   * Keyset cursor for the NEXT page, or null at the end. OPAQUE — do not parse,
   * construct or persist assumptions about it; the server ignores a malformed one
   * and serves the first page rather than erroring.
   *
   * Keyset rather than offset because `last_activity_at` moves: every turn bumps
   * the thread to the top, so an offset page-2 would re-show a row from page 1 and
   * silently drop the one at the boundary.
   */
  next_cursor?: string | null;
}>;
export type SessionDetailResponse = Gated<{ messages: ChatSessionMessage[] }>;
export type CreateSessionResponse = Gated<{ sessionId: string }>;

/** Narrowing helper — `enabled === false` is the only disabled shape. */
export function isCapabilityDisabled(res: { enabled?: boolean } | undefined): boolean {
  return res?.enabled === false;
}

export const crmSkyAgentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createSkyAgentSession: builder.mutation<CreateSessionResponse, void>({
      query: () => ({ url: "/crm/skyagent/sessions", method: "POST" }),
      invalidatesTags: ["CrmSkyAgentSessions"],
    }),

    /**
     * The history rail, KEYSET-paginated and accumulated into one cache entry.
     *
     * Three pieces make that work, and all three are required:
     *
     *  - `serializeQueryArgs` drops `cursor` from the cache key, so every page
     *    lands in the SAME entry instead of creating one entry per cursor (which
     *    is what makes the rail keep the rows already on screen).
     *  - `merge` appends the incoming page, de-duplicating by `sessionId`. The
     *    dedupe is not belt-and-braces: `last_activity_at` moves under us, so a
     *    thread bumped between two page fetches can legitimately arrive twice,
     *    and React would throw on the duplicate key.
     *  - `forceRefetch` fires only when the cursor actually changed; without it
     *    RTK serves the merged entry from cache and the next page never loads.
     *
     * The previous implementation refetched a GROWING limit (20 → 40 → 60), which
     * re-transferred every row already on screen on each "Show more" and could
     * still drop a row at the page boundary when a thread was bumped mid-scroll.
     */
    listSkyAgentSessions: builder.query<
      SessionListResponse,
      { limit?: number; cursor?: string | null } | void
    >({
      query: (args) => ({
        url: "/crm/skyagent/sessions",
        method: "GET",
        params: {
          ...(args && args.limit ? { limit: args.limit } : {}),
          ...(args && args.cursor ? { cursor: args.cursor } : {}),
        },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${(queryArgs && queryArgs.limit) || ""}`,
      merge: (current, incoming, { arg }) => {
        // A first page (no cursor) REPLACES: that is what a refetch after a turn,
        // or a capability flip, has to do. Only a cursored page appends.
        if (!arg || !arg.cursor) return incoming;
        if ("enabled" in current && current.enabled === false) return incoming;
        if ("enabled" in incoming && incoming.enabled === false) return incoming;
        const seen = new Set(current.sessions.map((s) => s.sessionId));
        return {
          ...incoming,
          sessions: [
            ...current.sessions,
            ...incoming.sessions.filter((s) => !seen.has(s.sessionId)),
          ],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        (currentArg?.cursor ?? null) !== (previousArg?.cursor ?? null),
      providesTags: ["CrmSkyAgentSessions"],
    }),

    /** Rename a thread. The rail auto-titles from the opening question, so a
     *  counselor's list fills with three near-identical rows. */
    renameSkyAgentSession: builder.mutation<
      { ok: true; title: string },
      { sessionId: string; title: string }
    >({
      query: ({ sessionId, title }) => ({
        url: `/crm/skyagent/sessions/${sessionId}`,
        method: "PATCH",
        body: { title },
      }),
      invalidatesTags: ["CrmSkyAgentSessions"],
    }),

    /** Delete a thread. SOFT on the server — the row stops being reachable, it
     *  does not stop existing, because the AI audit trail references it. */
    deleteSkyAgentSession: builder.mutation<{ ok: true }, string>({
      query: (sessionId) => ({
        url: `/crm/skyagent/sessions/${sessionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CrmSkyAgentSessions"],
    }),

    getSkyAgentSession: builder.query<SessionDetailResponse, string>({
      query: (sessionId) => ({
        url: `/crm/skyagent/sessions/${sessionId}`,
        method: "GET",
      }),
      providesTags: (_r, _e, sessionId) => [{ type: "CrmSkyAgentSession" as const, id: sessionId }],
    }),
  }),
});

export const {
  useCreateSkyAgentSessionMutation,
  useListSkyAgentSessionsQuery,
  useLazyGetSkyAgentSessionQuery,
  useRenameSkyAgentSessionMutation,
  useDeleteSkyAgentSessionMutation,
} = crmSkyAgentApi;
